
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { IoArrowBack, IoAlertCircleOutline, IoChevronDown, IoSearch, IoAttachOutline, IoTrashOutline, IoLockClosedOutline, IoCloudUploadOutline, IoGitNetworkOutline, IoAddCircleOutline } from 'react-icons/io5';
import './CreateTaskPagestyle.css';

// --- Configuration from Environment Variables ---
// These variables store the base URLs for different microservices, loaded from the project's .env file.
// This approach makes the application portable across different environments (development, staging, production).
const SWAGGER_BASE_URL = process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL;
const NODE_BASE_URL = process.env.REACT_APP_NODE_SERVICE_BASE_URL;
const ASSET_BASE_URL = process.env.REACT_APP_ASSET_SERVICE_BASE_URL;
const AUTH_TOKEN_KEY = 'zTaskAuthToken'; // The key used to store the user's authentication token in localStorage.

// --- API Helper Functions ---
// This section centralizes all API interactions, making the code cleaner and easier to manage.

/**
 * Creates and returns standard request headers for API calls.
 * @param {boolean} isFormData - If true, sets headers for multipart/form-data (for file uploads). Otherwise, sets headers for JSON.
 * @returns {HeadersInit} The headers object for the fetch request.
 */
const getApiHeaders = (isFormData = false) => {
    const headers = { 'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}` };
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
        headers['Accept'] = 'application/json';
    }
    return headers;
};

// Generic fetch wrapper to handle common error patterns.
const apiFetch = async (url, options) => {
    const response = await fetch(url, options);
    if (!response.ok) {
        // Try to parse a JSON error message from the response, otherwise throw a generic error.
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Request failed with status ${response.status}`);
    }
    return response.json();
};


// Fetches company-specific details, such as the company keyword.
const getCompanyDetails = async () => apiFetch(`${SWAGGER_BASE_URL}/api/get-company`, { headers: getApiHeaders() });
// Fetches a list of all users.
const getUsers = async () => { const data = await apiFetch(`${SWAGGER_BASE_URL}/api/get-users`, { headers: getApiHeaders() }); return data.userData || []; };
// Sends a POST request to create a new task.
const createTask = async (payload) => apiFetch(`${SWAGGER_BASE_URL}/api/create-task`, { method: 'POST', headers: getApiHeaders(), body: JSON.stringify(payload) });
// Fetches a list of all projects.
const getProjects = async () => { const data = await apiFetch(`${SWAGGER_BASE_URL}/api/get-project`, { headers: getApiHeaders() }); return data.projects; };
// Fetches projects associated with a specific customer ID.
const getProjectsByCustomerId = async (customerId) => apiFetch(`${SWAGGER_BASE_URL}/api/get-projects-by-customer-id?customerId=${customerId}`, { headers: getApiHeaders() });
// Fetches project and customer details for a given array of asset IDs.
const getProjectAndCustomerByAssetId = async (assetIds) => {
    if (!assetIds || assetIds.length === 0) {
        return Promise.resolve({ data: null });
    }
    const params = new URLSearchParams();
    assetIds.forEach(id => params.append('assetId', id));
    return apiFetch(`${SWAGGER_BASE_URL}/api/get-project-and-customer-by-asset-id?${params.toString()}`, { headers: getApiHeaders() });
};
// Fetches a list of all customers.
const getCustomers = async () => apiFetch(`${NODE_BASE_URL}/customer/api/customers/`, { headers: getApiHeaders() });
// Fetches a list of all assets.
const getAssets = async () => apiFetch(`${ASSET_BASE_URL}/api/assets/`, { headers: getApiHeaders() });
// A generic function to fetch distinct values from a specific column in a specific table.
const getColumnData = async (table, column) => apiFetch(`${NODE_BASE_URL}/api/get-column/${table}/${column}`, { headers: getApiHeaders() });
// Fetches the entire user hierarchy tree.
const getFullHierarchyTree = async () => apiFetch(`${SWAGGER_BASE_URL}/api-get-full-hierarchy-tree`, { headers: getApiHeaders() });
// Fetches the reporting managers for a given user ID.
const getReportingManagers = async (userId) => { const data = await apiFetch(`${SWAGGER_BASE_URL}/api/get-reporting-managers-by-userId?userId=${userId}`, { headers: getApiHeaders() }); return data.message || []; };
// Checks a boolean flag to determine if the "Projects" module is enabled.
const getProjectsModuleBoolean = async () => { const data = await apiFetch(`${NODE_BASE_URL}/api/get-column/common_master/module_boolean?module_name=projects`, { headers: getApiHeaders() }); return data?.[0]?.value === true; };

/**
 * Uploads attachment files for a given task.
 * @param {string} taskId - The ID of the task to associate the files with.
 * @param {string} moduleName - The name of the module (e.g., 'attachment_master').
 * @param {File[]} files - An array of File objects to upload.
 * @param {string[]} descriptions - An array of descriptions corresponding to each file.
 * @returns {Promise<any>} The result of the upload API call.
 */
const uploadAttachments = async (taskId, moduleName, files, descriptions) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    descriptions.forEach(desc => formData.append('descriptions[]', desc || '')); // Append descriptions
    return apiFetch(`${NODE_BASE_URL}/attachment/api/attachments/${taskId}/${moduleName}`, { method: 'POST', headers: getApiHeaders(true), body: formData });
};


// Simplified functions for fetching common dropdown data.
const getStatuses = () => getColumnData('common_master', 'current_status');
const getPriorities = () => getColumnData('common_master', 'priority_in_daily_task');
const getCategories = () => getColumnData('common_master', 'category');

// --- Constants & Helper Functions ---
// Centralized constants and pure utility functions used throughout the component.

// Defines permission strings to avoid typos and centralize permission management.
const PERMISSIONS = { CREATE_TASK: 'CREATE TASK', VIEW_USER: 'VIEW USER', VIEW_PROJECT: 'VIEW PROJECT', VIEW_CATEGORY: 'VIEW CATEGORY', VIEW_STATUS: 'VIEW STATUS', VIEW_PRIORITY: 'VIEW PRIORITY' };
const DESCRIPTION_MAX_LENGTH = 5000;

// Helper function to pad numbers with a leading zero if they are less than 10.
const pad = (num) => num.toString().padStart(2, '0');

// Date and time constants for dropdowns.
const currentYear = new Date().getFullYear();
const endYear = 2099;
const yearRange = endYear - currentYear + 1;
const years = Array.from({ length: yearRange > 0 ? yearRange : 1 }, (_, i) => currentYear + i);
const allMonths = [ { value: '1', name: 'Jan' }, { value: '2', name: 'Feb' }, { value: '3', name: 'Mar' }, { value: '4', name: 'Apr' }, { value: '5', name: 'May' }, { value: '6', name: 'Jun' }, { value: '7', name: 'Jul' }, { value: '8', name: 'Aug' }, { value: '9', name: 'Sep' }, { value: '10', name: 'Oct' }, { value: '11', name: 'Nov' }, { value: '12', name: 'Dec' } ];
const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();
const allHours = Array.from({ length: 24 }, (_, i) => pad(i));
const allMinutes = Array.from({ length: 60 }, (_, i) => pad(i));

/**
 * Applies basic grammar correction to a string: trims start, capitalizes the first letter,
 * and capitalizes letters following sentence-ending punctuation.
 * @param {string} text - The input text.
 * @returns {string} The corrected text.
 */
const autoCorrectGrammar = (text) => {
    if (typeof text !== 'string' || !text.trim() && text.length > 0) return text;
    let correctedText = text.trimStart();
    if (correctedText.length > 0) {
        correctedText = correctedText.charAt(0).toUpperCase() + correctedText.slice(1);
    }
    // Capitalize the letter after a period, question mark, or exclamation mark followed by a space.
    return correctedText.replace(/([.!?]\s+)([a-z])/g, (match, p, letter) => p + letter.toUpperCase());
};

/**
 * Sorts an array of objects alphabetically based on a specified key.
 * @param {Array<Object>} array - The array to sort.
 * @param {string} key - The object key to sort by.
 * @returns {Array<Object>} A new, sorted array.
 */
const sortAlphabetically = (array, key) => {
    if (!Array.isArray(array)) return [];
    return [...array].sort((a, b) => {
        const valA = String(a[key] || '').toLowerCase();
        const valB = String(b[key] || '').toLowerCase();
        return valA.localeCompare(valB);
    });
};


// --- Hierarchy Helper Functions ---
// These functions are used to work with the nested user hierarchy data structure.

/**
 * Recursively searches a hierarchy tree to find a user node by name.
 * @param {Array<Object>} tree - The hierarchy tree (or a branch of it).
 * @param {string} userName - The name of the user to find.
 * @returns {Object|null} The found user node or null if not found.
 */
const findUserInHierarchy = (tree, userName) => {
    for (const node of tree) {
        if (node.name === userName) return node;
        if (node.children && node.children.length > 0) {
            const foundUser = findUserInHierarchy(node.children, userName);
            if (foundUser) return foundUser;
        }
    }
    return null;
};

/**
 * Recursively collects all subordinates (children, grandchildren, etc.) of a given user node.
 * @param {Object} userNode - The user node from the hierarchy tree.
 * @returns {Array<Object>} A flat array of all subordinate user nodes.
 */
const getAllSubordinates = (userNode) => {
    let subordinates = [];
    if (userNode && userNode.children && userNode.children.length > 0) {
        for (const child of userNode.children) {
            subordinates.push(child);
            subordinates = subordinates.concat(getAllSubordinates(child));
        }
    }
    return subordinates;
};

/**
 * Recursively filters a hierarchy tree to include only active users and their active children.
 * @param {Array<Object>} nodes - The array of hierarchy nodes to filter.
 * @returns {Array<Object>} A new tree containing only active nodes.
 */
const filterActiveHierarchy = (nodes) => {
    if (!Array.isArray(nodes)) return [];
    return nodes
      .filter(node => node.active === true)
      .map(node => ({
        ...node,
        children: filterActiveHierarchy(node.children),
      }));
};


// --- Project Filtering Helper ---
/**
 * Filters a list of projects based on the user's role and company-specific rules.
 * @param {Array<Object>} projects - The list of all projects.
 * @param {Object} user - The currently logged-in user object.
 * @param {boolean} showAll - A flag to bypass filtering and show all projects.
 * @param {string} companyKeyword - A keyword for company-specific logic (e.g., 'ZO').
 * @returns {Array<Object>} The filtered list of projects.
 */
const filterProjectsForUser = (projects, user, showAll, companyKeyword) => {
    if (!user?.id || !Array.isArray(projects)) {
        return [];
    }
    const userId = user.id;

    // 'ZO' specific logic: User sees projects they created OR are assigned to.
    if (companyKeyword === 'ZO') {
        return projects.filter(proj =>
            String(proj.createdBy) === String(userId) ||
            (Array.isArray(proj.assigneeId) && proj.assigneeId.some(id => String(id) === String(userId)))
        );
    }

    // If the global 'show all projects' flag is true, return all projects.
    if (showAll) {
        return projects;
    } else {
        // Default behavior: User only sees projects they created.
        return projects.filter(proj => String(proj.createdBy) === String(userId));
    }
};


// --- Reusable Sub-Components ---
// These are smaller, self-contained components used to build the main page.

/**
 * A skeleton loader component that displays a shimmering placeholder UI
 * while the main page data is being fetched.
 */
const CreateTaskPageSkeleton = () => ( <div className="create-task-page"><div className="page-header"><div className="shimmer-line back-link-skeleton"></div><div className="shimmer-line title-skeleton"></div><div className="shimmer-line subtitle-skeleton"></div></div><div className="create-task-form"><div className="form-main-panel"><div className="shimmer-line section-header-skeleton"></div>{[...Array(6)].map((_, i) => (<div key={i} className="form-group-skeleton"><div className="shimmer-line label-skeleton"></div><div className="shimmer-block input-skeleton"></div></div>))}<div className="form-group-skeleton"><div className="shimmer-line label-skeleton"></div><div className="shimmer-block textarea-skeleton"></div></div></div><div className="form-side-panel"><div className="shimmer-line section-header-skeleton"></div>{[...Array(4)].map((_, i) => (<div key={i} className="form-group-skeleton"><div className="shimmer-line label-skeleton"></div><div className="shimmer-block input-skeleton"></div></div>))}</div></div></div> );

/**
 * A custom hook that triggers a callback when a click is detected outside of the referenced element.
 * Useful for closing dropdowns or modals.
 * @param {React.RefObject} ref - A ref attached to the element to monitor.
 * @param {Function} callback - The function to call on an outside click.
 */
const useOutsideAlerter = (ref, callback) => { useEffect(() => { const handleClickOutside = (event) => { if (ref.current && !ref.current.contains(event.target)) { callback(); } }; document.addEventListener('mousedown', handleClickOutside); return () => document.removeEventListener('mousedown', handleClickOutside); }, [ref, callback]); };

/**
 * A feature-rich, searchable dropdown component.
 * Supports single/multi-select, search, clear selection, disabled state, and error handling.
 */
const SearchableDropdown = ({ options, selectedValues, onChange, placeholder, isMultiSelect = false, labelField = 'name', valueField = 'id', disabled = false, error = false, dataLoadError = null, allowClear = false }) => { const [isOpen, setIsOpen] = useState(false); const [searchTerm, setSearchTerm] = useState(''); const dropdownRef = useRef(null); useOutsideAlerter(dropdownRef, () => setIsOpen(false)); const filteredOptions = options.filter(option => option && typeof option[labelField] === 'string' ? option[labelField].toLowerCase().includes(searchTerm.toLowerCase()) : false); const handleSelect = (optionValue) => { if (isMultiSelect) { const newValues = selectedValues.includes(optionValue) ? selectedValues.filter(v => v !== optionValue) : [...selectedValues, optionValue]; onChange(newValues); } else { onChange(optionValue); setIsOpen(false); } }; const getDisplayValue = () => { if (isMultiSelect) { if (selectedValues.length === 0) return placeholder; if (selectedValues.length === 1) { const selectedOption = options.find(opt => opt[valueField] === selectedValues[0]); return selectedOption ? selectedOption[labelField] : '1 selected'; } return `${selectedValues.length} items selected`; } else { const selectedOption = options.find(opt => String(opt[valueField]) === String(selectedValues)); return selectedOption ? selectedOption[labelField] : placeholder; } }; return ( <div className="searchable-dropdown-container" ref={dropdownRef}> <button type="button" className={`dropdown-trigger ${error ? 'invalid-field' : ''}`} onClick={() => !disabled && setIsOpen(!isOpen)} disabled={disabled}> <span>{getDisplayValue()}</span> <IoChevronDown className={`dropdown-chevron ${isOpen ? 'open' : ''}`} /> </button> {isOpen && ( <div className="dropdown-panel"> <div className="dropdown-search-container"> <input type="text" className="dropdown-search-input" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus /> <IoSearch className="search-icon" /> </div> <ul className="dropdown-options-list"> {dataLoadError && <li className="dropdown-option-message">{dataLoadError}</li>} {!dataLoadError && allowClear && !isMultiSelect && ( <li className="dropdown-option clear-option" onClick={() => handleSelect('')}> — Clear Selection — </li> )} {!dataLoadError && filteredOptions.length > 0 ? ( filteredOptions.map(option => { const isSelected = isMultiSelect ? selectedValues.includes(option[valueField]) : String(selectedValues) === String(option[valueField]); return ( <li key={option[valueField]} className={`dropdown-option ${isSelected ? 'selected' : ''}`} onClick={() => handleSelect(option[valueField])}> {isMultiSelect && ( <input type="checkbox" checked={isSelected} readOnly className="dropdown-checkbox" /> )} {option[labelField]} </li> ); }) ) : ( !dataLoadError && <li className="dropdown-option-message">No results found</li> )} </ul> </div> )} </div> ); };

/**
 * A simple component to display a message when the user lacks the necessary permissions to view the page.
 */
const AccessDenied = () => ( <div className="create-task-page"><div className="page-header"><Link to="/tasks" className="back-link"><IoArrowBack /> Back to Tasks</Link><h1>Access Denied</h1></div><div className="error-message icon-flex" role="alert" style={{ marginTop: '20px' }}><IoLockClosedOutline size={24} style={{ marginRight: '10px' }} />You do not have the required permission to create a new task. Please contact your administrator.</div></div> );

// --- Main Page Component: CreateTaskPage ---

const CreateTaskPage = () => {
    // --- React Router & Permissions Hooks ---
    const navigate = useNavigate(); // Hook for programmatic navigation.
    const location = useLocation(); // Hook to access location state (e.g., passed data from another page).
    const { user, hasPermission, isLoading: isPermissionsLoading } = usePermissions(); // Custom hook to get user data and check permissions.

    // --- Component Mode & Initial Data from Location State ---
    // These values are passed from the previous page via `navigate('/path', { state: { ... } })`.
    const parentTask = location.state?.parentTask; // The parent task details if creating a subtask.
    const isSubtaskOnlyMode = location.state?.subtaskOnly === true; // A flag to render the page in "add subtask" mode.
    const taskToClone = location.state?.taskToClone; // The task details if cloning an existing task.

    // --- State Management ---

    // Parent Task State: Manages all data related to the main task being created.
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [selectedAssigneeId, setSelectedAssigneeId] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedPriority, setSelectedPriority] = useState('');
    const [dueDate, setDueDate] = useState(new Date());
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedAssetIds, setSelectedAssetIds] = useState([]);
    const [remindMeDays, setRemindMeDays] = useState(1);
    const [files, setFiles] = useState([]);
    const [fileDescriptions, setFileDescriptions] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [selectedProject, setSelectedProject] = useState('');
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [reportingManagers, setReportingManagers] = useState([]);
    const [selectedReportingManagerId, setSelectedReportingManagerId] = useState('');
    const [selectedTaggedUserIds, setSelectedTaggedUserIds] = useState([]);

    // Subtask State: Manages an array of subtask objects.
    const [subtasks, setSubtasks] = useState([]);
    
    // Data & Dropdown Options State: Stores data fetched from APIs.
    const [assets, setAssets] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [assigneeOptions, setAssigneeOptions] = useState([]); // The dynamically filtered list of users for the assignee dropdown.
    const [statuses, setStatuses] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [categories, setCategories] = useState([]);
    const [allProjects, setAllProjects] = useState([]); // Master list of all projects.
    const [projectsPool, setProjectsPool] = useState([]); // A pool of projects to filter from (either all projects or customer-specific).
    const [filteredProjects, setFilteredProjects] = useState([]); // Final list of projects for the dropdown after all filters are applied.
    const [customers, setCustomers] = useState([]);
    const [hierarchy, setHierarchy] = useState(null); // The user hierarchy tree.
    
    // UI & Loading State: Manages the component's UI state (loading, errors, submission status, etc.).
    const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
    const [isFetchingProjects, setIsFetchingProjects] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dataLoadErrors, setDataLoadErrors] = useState({}); // Stores errors for specific data fetches (e.g., users, projects).
    const [formValidationError, setFormValidationError] = useState(null); // A general validation error message for the form.
    const [invalidFields, setInvalidFields] = useState([]); // An array of field names that have validation errors.
    const [successMessage, setSuccessMessage] = useState('');
    const [initialDataLoadAttempted, setInitialDataLoadAttempted] = useState(false); // Flag to prevent multiple initial data fetches.
    const [isDragging, setIsDragging] = useState(false); // For the file drop zone UI.
    const [isFetchingManagers, setIsFetchingManagers] = useState(false);
    const [isFetchingProjectForAsset, setIsFetchingProjectForAsset] = useState(false);
    const [showAllProjects, setShowAllProjects] = useState(false); // Flag based on company settings.
    const [companyKeyword, setCompanyKeyword] = useState(''); // Company identifier for special logic.

    // Date/Time Dropdown State: Manages the dynamic options for date/time selectors.
    const [availableYears, setAvailableYears] = useState(years);
    const [filteredMonths, setFilteredMonths] = useState(allMonths);
    const [filteredDays, setFilteredDays] = useState([]);
    const [filteredHours, setFilteredHours] = useState(allHours);
    const [filteredMinutes, setFilteredMinutes] = useState(allMinutes);
    const [maxReminderDays, setMaxReminderDays] = useState(365);


    // --- Memoized Values ---
    // `useMemo` is used to optimize performance by caching the result of expensive calculations.
    // The calculation is only re-run when one of the dependencies in the array changes.
    const taggableUsers = useMemo(() => {
        // Creates a list of users available for tagging, excluding the currently selected assignee.
        if (!selectedAssigneeId) {
            return allUsers;
        }
        return allUsers.filter(user => String(user.id) !== String(selectedAssigneeId));
    }, [allUsers, selectedAssigneeId]);


    // --- Data Fetching and Initialization ---

    /**
     * Fetches all necessary initial data for the component in parallel.
     * This function is wrapped in `useCallback` to prevent it from being recreated on every render,
     * which is important as it's a dependency of a `useEffect`.
     */
    const fetchInitialData = useCallback(async (loggedInUser) => {
        setIsFetchingInitialData(true);
        setDataLoadErrors({});
        setInitialDataLoadAttempted(true);

        const canViewUsers = hasPermission(PERMISSIONS.VIEW_USER);
        const selfUserObject = { id: String(loggedInUser.id), name: loggedInUser.name, email: loggedInUser.email, department: loggedInUser.department, active: true };

        // Create a map of API calls to be executed.
        const dataFetchers = { 
            company: getCompanyDetails(),
            users: canViewUsers ? getUsers() : Promise.resolve([selfUserObject]), // Fetch all users or just the logged-in user.
            statuses: hasPermission(PERMISSIONS.VIEW_STATUS) ? getStatuses() : Promise.resolve(null), 
            priorities: hasPermission(PERMISSIONS.VIEW_PRIORITY) ? getPriorities() : Promise.resolve(null), 
            categories: hasPermission(PERMISSIONS.VIEW_CATEGORY) ? getCategories() : Promise.resolve(null), 
            projects: hasPermission(PERMISSIONS.VIEW_PROJECT) ? getProjects() : Promise.resolve(null), 
            customers: hasPermission(PERMISSIONS.VIEW_PROJECT) ? getCustomers() : Promise.resolve(null), 
            assets: getAssets(), 
            hierarchy: getFullHierarchyTree(), 
            projectModuleBoolean: getProjectsModuleBoolean() 
        };

        // `Promise.allSettled` runs all promises concurrently and waits for all to complete, regardless of success or failure.
        const results = await Promise.allSettled(Object.values(dataFetchers));
        const [ companyRes, usersRes, statusesRes, prioritiesRes, categoriesRes, projectsRes, customersRes, assetsRes, hierarchyRes, projectModuleBooleanRes ] = results;
        
        // Process results for each API call, updating state or setting error messages.
        if (companyRes.status === 'fulfilled' && companyRes.value.message) { setCompanyKeyword(companyRes.value.message[0]?.companyKeyword || ''); }
        if (projectModuleBooleanRes.status === 'fulfilled') { setShowAllProjects(projectModuleBooleanRes.value); }
        if (statusesRes.status === 'fulfilled') { const formatted = (statusesRes.value || []).map(s => ({ ...s, displayValue: s.value.replace(/_/g, ' ') })); const sorted = sortAlphabetically(formatted, 'displayValue'); const open = sorted.find(s => s.value.toLowerCase() === 'open'); setSelectedStatus(open ? open.value : (sorted.length > 0 ? sorted[0].value : '')); setStatuses(sorted); } else { setDataLoadErrors(prev => ({ ...prev, statuses: 'No permission to view statuses.' })); }
        if (prioritiesRes.status === 'fulfilled' && prioritiesRes.value) { const sorted = sortAlphabetically(prioritiesRes.value, 'value'); const medium = sorted.find(p => p.value.toUpperCase() === 'MEDIUM'); setSelectedPriority(medium ? medium.value : (sorted.length > 0 ? sorted[0].value : '')); setPriorities(sorted); } else { setDataLoadErrors(prev => ({ ...prev, priorities: 'No permission to view priorities.' })); }
        if (categoriesRes.status === 'fulfilled' && categoriesRes.value) { const sorted = sortAlphabetically(categoriesRes.value, 'value'); setCategories(sorted); if (sorted.length > 0) { setSelectedCategory(sorted[0].value); } } else { setDataLoadErrors(prev => ({ ...prev, categories: 'No permission to view categories.' })); }
        if (usersRes.status === 'fulfilled') { const activeUsers = (usersRes.value || []).filter(u => u.active === true); setAllUsers(sortAlphabetically(activeUsers, 'name').map(u => ({ ...u, id: String(u.id) }))); } else { setDataLoadErrors(prev => ({ ...prev, users: 'Could not load users.' })); }
        if (projectsRes.status === 'fulfilled' && projectsRes.value) { const allFetchedProjects = (projectsRes.value || []).map(p => ({ ...p, id: String(p.projectId), name: p.projectName })); const sorted = sortAlphabetically(allFetchedProjects, 'name'); setAllProjects(sorted); setProjectsPool(sorted); } else { setDataLoadErrors(prev => ({ ...prev, projects: 'No permission to view projects.' })); }
        if (customersRes.status === 'fulfilled' && customersRes.value) { const formatted = (customersRes.value || []).filter(c => c.active).map(c => ({ id: String(c.id), name: c.name })); setCustomers(sortAlphabetically(formatted, 'name')); } else { setDataLoadErrors(prev => ({ ...prev, customers: 'No permission to view customers.' })); }
        if (assetsRes.status === 'fulfilled' && assetsRes.value) { const formattedAssets = (assetsRes.value || []).map(asset => { const displayParts = [ asset.name, asset.serial_number, asset.asset_code, asset.registration_number ].filter(Boolean); return { id: asset.id, name: asset.name, asset_code: asset.asset_code, value: displayParts.join(' | ') }; }); setAssets(sortAlphabetically(formattedAssets, 'value')); } else { setDataLoadErrors(prev => ({ ...prev, assets: 'Could not load asset details.' })); }
        if (hierarchyRes.status === 'fulfilled' && hierarchyRes.value) { setHierarchy(filterActiveHierarchy(hierarchyRes.value?.tree || [])); } else { setDataLoadErrors(prev => ({...prev, hierarchy: 'Could not load hierarchy.'})); }

        // Set the logged-in user as the default assignee.
        if (loggedInUser) {
            setSelectedAssigneeId(String(loggedInUser.id));
            const defaultUser = (usersRes.status === 'fulfilled' && usersRes.value || []).find(u => String(u.id) === String(loggedInUser.id));
            setSelectedDepartment(defaultUser?.department?.departmentName || loggedInUser.department?.departmentName || 'N/A');
        }
        
        setIsFetchingInitialData(false);
    }, [hasPermission]); // Dependency: re-run only if `hasPermission` function instance changes.

    // This `useEffect` triggers the initial data fetch once the user's permissions are loaded.
    useEffect(() => {
        if (!isPermissionsLoading && user && !initialDataLoadAttempted) {
            if (hasPermission(PERMISSIONS.CREATE_TASK)) {
                fetchInitialData(user);
            } else {
                setIsFetchingInitialData(false); // If no permission, stop the loading state.
            }
        }
    }, [user, isPermissionsLoading, initialDataLoadAttempted, hasPermission, fetchInitialData]);


    // --- Event Handlers ---

    /**
     * A generic handler to update state and clear validation errors for a field.
     * @param {string} fieldName - The name of the field to clear from the `invalidFields` array.
     * @param {*} value - The new value for the state.
     * @param {Function} setterFunction - The state setter function to call.
     */
    const handleFieldChange = (fieldName, value, setterFunction) => {
        setterFunction(value);
        if (invalidFields.includes(fieldName)) {
            const updatedInvalidFields = invalidFields.filter(f => f !== fieldName);
            setInvalidFields(updatedInvalidFields);
            if (updatedInvalidFields.length === 0 && formValidationError) {
                setFormValidationError(null);
            }
        }
    };

    /**
     * Handles changes to the assignee dropdown.
     * Updates the assignee ID, department, and clears the reporting manager.
     */
    const handleAssigneeChange = (userId) => {
        handleFieldChange('assignee', userId, setSelectedAssigneeId);
        const selectedUser = allUsers.find(user => String(user.id) === String(userId));
        setSelectedDepartment(selectedUser ? (selectedUser.department?.departmentName || 'N/A') : 'N/A');
        setSelectedReportingManagerId(''); // Reset manager when assignee changes.
    };
    
    /**
     * Handles changes to the customer dropdown.
     * Fetches projects specific to the selected customer or resets to all projects if cleared.
     */
    const handleCustomerChange = useCallback(async (customerId) => {
        setSelectedCustomerId(customerId);
        setSelectedProject(''); // Clear project when customer changes.
        setDataLoadErrors(prev => ({...prev, projects: null}));
        if (customerId) {
            setIsFetchingProjects(true);
            try {
                const data = await getProjectsByCustomerId(customerId);
                const customerProjects = (data.projects || []).map(p => ({ ...p, id: String(p.projectId), name: p.projectName }));
                setProjectsPool(sortAlphabetically(customerProjects, 'name')); // Update the project pool.
            } catch (error) {
                console.error("Failed to fetch projects for customer", error);
                setDataLoadErrors(prev => ({ ...prev, projects: 'no load project found.' }));
                setProjectsPool([]);
            } finally {
                setIsFetchingProjects(false);
            }
        } else {
            // If customer is cleared, reset the pool to all available projects.
            setProjectsPool(allProjects);
        }
    }, [allProjects]); // Dependency: `allProjects` master list.
    
    /**
     * Handles changes to the project dropdown.
     * Also auto-selects the customer if a project with a customer is chosen.
     */
    const handleProjectChange = (projectId) => {
        setSelectedProject(projectId);
        if (projectId) {
            const project = allProjects.find(p => String(p.id) === String(projectId));
            // If the selected project has a customer, and it's different from the currently selected one, update the customer.
            if (project && project.customerId && String(project.customerId) !== selectedCustomerId) {
                setSelectedCustomerId(String(project.customerId));
            }
        }
    };
    

    // --- Complex Logic Effects ---
    // These `useEffect` hooks contain the core reactive logic of the component.

    // Effect to determine the FINAL list of projects for the dropdown.
    // It runs whenever the assignee, project pool, or company settings change.
    useEffect(() => {
        const userRole = user?.role?.name.toUpperCase();
        let projectsToFilter = projectsPool;

        // SUPER_ADMIN sees all projects from the current pool (all or customer-specific).
        if (userRole === 'SUPER_ADMIN') {
            setFilteredProjects(projectsToFilter);
            return;
        }
    
        // 'ZO' specific logic: Projects are filtered based on the *selected assignee*.
        if (companyKeyword === 'ZO' && selectedAssigneeId) {
            const assigneeProjects = projectsToFilter.filter(proj =>
                (Array.isArray(proj.assigneeId) && proj.assigneeId.some(id => String(id) === String(selectedAssigneeId))) ||
                String(proj.createdBy) === String(selectedAssigneeId)
            );
            setFilteredProjects(assigneeProjects);
            // If the currently selected project is no longer in the valid list, clear it.
            if (selectedProject && !assigneeProjects.some(p => String(p.id) === String(selectedProject))) {
                setSelectedProject('');
            }
        } else {
            // Default behavior for other companies/roles: filter projects based on the *logged-in user*.
            setFilteredProjects(filterProjectsForUser(projectsToFilter, user, showAllProjects, companyKeyword));
        }
    }, [selectedAssigneeId, projectsPool, companyKeyword, user, showAllProjects, selectedProject]); // Re-calculates when any of these change.

    // Effect to pre-populate the form when cloning a task.
    // Runs only once after the initial data has been fetched if `taskToClone` exists.
    useEffect(() => {
        if (!isFetchingInitialData && taskToClone) {
            setTitle(`Clone of: ${taskToClone.title}`);
            setDescription(taskToClone.description || '');
            setSelectedCategory(taskToClone.category || '');
            setSelectedPriority(taskToClone.priority || '');
            if (taskToClone.assigneeId) { setSelectedAssigneeId(String(taskToClone.assigneeId)); const assignee = allUsers.find(u => String(u.id) === String(taskToClone.assigneeId)); if (assignee) { setSelectedDepartment(assignee.department?.departmentName || 'N/A'); } }
            if (taskToClone.dueDate) { const clonedDueDate = new Date(taskToClone.dueDate); if (clonedDueDate > new Date()) { setDueDate(clonedDueDate); } }
            if (taskToClone.projectName && allProjects.length > 0) { const project = allProjects.find(p => p.name === taskToClone.projectName); if (project) { setSelectedProject(project.id); if (project.customerId) { setSelectedCustomerId(String(project.customerId)); } } }
            if (Array.isArray(taskToClone.taggedUsers)) {
                setSelectedTaggedUserIds(taskToClone.taggedUsers.map(String));
            }
            // Clear the clone data from location state to prevent re-cloning on refresh.
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [isFetchingInitialData, taskToClone, allProjects, allUsers, navigate, location.pathname]);

    // Effect to set up the form when in "Subtask Only Mode".
    // This mode disables parent fields and pre-fills them with parent task data.
    useEffect(() => {
        if (isSubtaskOnlyMode && parentTask && allUsers.length > 0) {
            // Pre-fill and disable parent task fields.
            setTitle(parentTask.title);
            setDescription(parentTask.description || '');
            setSelectedCategory(parentTask.category || '');
            setSelectedPriority(parentTask.priority || '');
            setSelectedStatus(parentTask.status ? parentTask.status.replace(/_/g, ' ') : '');
            
            const parentAssignee = allUsers.find(u => u.name === parentTask.assigneeName);
            if(parentAssignee) {
                setSelectedAssigneeId(String(parentAssignee.id));
                setSelectedDepartment(parentAssignee.department?.departmentName || 'N/A');
            }
            if (parentTask.reportingManagerId) { setSelectedReportingManagerId(String(parentTask.reportingManagerId)); }
            if (parentTask.dueDate) { setDueDate(new Date(parentTask.dueDate)); }
            if(parentTask.customerId) { setSelectedCustomerId(String(parentTask.customerId)); }
            if(parentTask.projectName && allProjects.length > 0) { const project = allProjects.find(p => p.name === parentTask.projectName); if(project) setSelectedProject(project.id); }
            
            // Automatically add the first subtask form.
            if(subtasks.length === 0) {
                handleAddSubtask();
            }
        }
    }, [isSubtaskOnlyMode, parentTask, allUsers, allProjects]); // Dependencies ensure this runs when data is ready.


    // Effect to determine the list of available assignees. This is a critical piece of logic.
    // It filters the `allUsers` list based on the user's role, hierarchy, and project selection.
    useEffect(() => {
        if (!user || !allUsers.length || !hierarchy) {
            setAssigneeOptions([]);
            return;
        }
    
        const userRole = user?.role?.name.toUpperCase();
        const self = allUsers.find(u => String(u.id) === String(user.id));
    
        // Rule 1: 'USER' role can only assign tasks to themselves. This rule has the highest priority.
        if (userRole === 'USER') {
            setAssigneeOptions(self ? [self] : []);
            return;
        }
    
        let assignableUsersPool = [];
    
        // Rule 2: 'SUPER_ADMIN' can assign to anyone from the list of all active users.
        if (userRole === 'SUPER_ADMIN') {
            assignableUsersPool = [...allUsers];
        } 
        // Rule 3: Other roles (e.g., managers) are governed by the hierarchy. They can assign to themselves or their subordinates.
        else {
            const currentUserNode = findUserInHierarchy(hierarchy, user.name);
            if (currentUserNode) {
                const subordinateUsers = getAllSubordinates(currentUserNode);
                const subordinateIds = new Set(subordinateUsers.map(u => String(u.id)));
                
                assignableUsersPool = allUsers.filter(assignee =>
                    String(assignee.id) === String(currentUserNode.id) || // User can assign to themself
                    subordinateIds.has(String(assignee.id))              // Or to their subordinates
                );
            } else {
                // Fallback: If the user isn't found in the hierarchy, they can only assign to themselves.
                assignableUsersPool = self ? [self] : [];
            }
        }
    
        // Optional Filter (Rule 4): For 'ZO' company, if a project is selected, the assignee list is further filtered
        // to only include users who are assigned to or created that project.
        if (companyKeyword === 'ZO' && selectedProject) {
            const project = allProjects.find(p => String(p.id) === String(selectedProject));
            if (project && Array.isArray(project.assigneeId) && project.assigneeId.length > 0) {
                const projectAssigneeIds = new Set(project.assigneeId.map(String));
                const projectCreatorId = String(project.createdBy);
                
                // Filter the already role-restricted pool.
                const finalFilteredUsers = assignableUsersPool.filter(u => 
                    projectAssigneeIds.has(String(u.id)) || String(u.id) === projectCreatorId
                );

                setAssigneeOptions(sortAlphabetically(finalFilteredUsers, 'name'));
            } else {
                 // If the project has no explicitly assigned users, the assignee list becomes empty.
                 setAssigneeOptions([]);
            }
        } else {
            // If no project is selected or for other companies, use the role-based pool.
            setAssigneeOptions(sortAlphabetically(assignableUsersPool, 'name'));
        }
    
    }, [user, hierarchy, allUsers, companyKeyword, selectedProject, allProjects]); // Re-runs whenever these core data points change.

    // Effect to manage the *value* of the selected assignee based on the available `assigneeOptions`.
    useEffect(() => {
        if (isSubtaskOnlyMode) return; // In subtask mode, the assignee is fixed.
        
        // If the currently selected assignee is no longer in the list of valid options, clear the selection.
        if (selectedAssigneeId && assigneeOptions.length > 0 && !assigneeOptions.some(opt => opt.id === selectedAssigneeId)) {
            setSelectedAssigneeId('');
        }
        
        // If there's only one valid option in the dropdown (e.g., a 'USER' can only select themself), auto-select it.
        if (assigneeOptions.length === 1 && selectedAssigneeId !== assigneeOptions[0].id) {
            setSelectedAssigneeId(assigneeOptions[0].id);
        }
    }, [assigneeOptions, selectedAssigneeId, isSubtaskOnlyMode]); // Runs when the options or the selection change.

    // Effect to fetch reporting managers for the selected PARENT task assignee.
    useEffect(() => {
        // In subtask only mode, this is handled differently to use parentTask data.
        if (isSubtaskOnlyMode) {
             if (parentTask?.reportingManagerId) {
                const manager = allUsers.find(u => String(u.id) === String(parentTask.reportingManagerId));
                if (manager) {
                    setReportingManagers([{id: String(manager.id), name: manager.name}]);
                    setSelectedReportingManagerId(String(manager.id));
                }
             }
            return;
        };

        const fetchManagersAndSetDefault = async (assigneeId) => {
            if (!assigneeId) {
                setReportingManagers([]);
                setSelectedReportingManagerId('');
                return;
            }
            setIsFetchingManagers(true);
            setDataLoadErrors(prev => ({ ...prev, managers: null })); 
            try {
                const managersData = await getReportingManagers(assigneeId);
                const self = allUsers.find(a => String(a.id) === String(assigneeId));
                let formattedManagers = [];

                if (managersData && managersData.length > 0) {
                    // Map manager emails from API response to full user objects from `allUsers`.
                    formattedManagers = managersData.map(manager => allUsers.find(a => a.email === manager.managerEmail)).filter(Boolean).map(user => ({ id: String(user.id), name: user.name }));
                }
                
                // If the API returns no managers, the user is their own manager.
                if (formattedManagers.length === 0 && self) {
                    formattedManagers.push({ id: String(self.id), name: self.name });
                }

                const sortedManagers = sortAlphabetically(formattedManagers, 'name');
                const loggedInUserId = String(user.id);
                const isCreatorInManagerList = sortedManagers.some(m => String(m.id) === loggedInUserId);

                setReportingManagers(sortedManagers);

                // Auto-select a default manager:
                // 1. If the creator is in the list, select them.
                // 2. Otherwise, if there's only one manager, select them.
                // 3. Otherwise, if the old selection is invalid, clear it.
                if (isCreatorInManagerList) {
                    setSelectedReportingManagerId(loggedInUserId);
                } else if (sortedManagers.length === 1) {
                    setSelectedReportingManagerId(sortedManagers[0].id);
                } else if (!sortedManagers.some(m => String(m.id) === selectedReportingManagerId)) {
                    setSelectedReportingManagerId('');
                }

            } catch (error) {
                console.error("Could not fetch reporting managers, applying self-as-manager fallback:", error);
                const self = allUsers.find(a => String(a.id) === String(assigneeId));
                if (self) {
                    setReportingManagers([{ id: String(self.id), name: self.name }]);
                    setSelectedReportingManagerId(String(self.id));
                }
            } finally {
                setIsFetchingManagers(false);
            }
        };
        fetchManagersAndSetDefault(selectedAssigneeId);
    }, [selectedAssigneeId, allUsers, isSubtaskOnlyMode, parentTask, user]); // Runs when assignee or user data changes.

    // Effect to auto-select project and customer when asset selection changes.
    useEffect(() => {
        // This effect should not run in subtask-only mode or during the initial data load.
        if (isSubtaskOnlyMode || isFetchingInitialData) {
            return;
        }

        // The core logic from the user request: only apply asset-based auto-filling
        // if the customer and project fields are both empty.
        if (selectedCustomerId || selectedProject) {
            return; // User has made a selection, so don't override it.
        }

        // If we reach here, customer and project are empty. Proceed with asset logic.

        // If no assets are selected, there's nothing to fill. Ensure the project pool is reset.
        if (selectedAssetIds.length === 0) {
            setProjectsPool(allProjects);
            return;
        }

        const fetchProjectForAssets = async (assetIds) => {
            setIsFetchingProjectForAsset(true);
            try {
                const response = await getProjectAndCustomerByAssetId(assetIds);

                // Check if the API returned valid, non-null data.
                if (response && Array.isArray(response.data) && response.data.length > 0 && response.data[0]) {
                    const { custId, projectId } = response.data[0];
                    if (custId) {
                        // Using handleCustomerChange will correctly filter the projects for that customer
                        await handleCustomerChange(String(custId));
                    }
                    if (projectId) {
                        setSelectedProject(String(projectId));
                    }
                } else {
                    // If API returns null or empty data, just reset the project pool. Don't clear fields as they are already empty.
                    setProjectsPool(allProjects);
                }
            } catch (error) {
                console.error("Failed to fetch project and customer for assets:", error);
                // On error, ensure fields are clear and reset the project pool.
                setSelectedCustomerId('');
                setSelectedProject('');
                setProjectsPool(allProjects);
            } finally {
                setIsFetchingProjectForAsset(false);
            }
        };

        fetchProjectForAssets(selectedAssetIds);

    }, [selectedAssetIds, selectedCustomerId, selectedProject, isSubtaskOnlyMode, isFetchingInitialData, allProjects, handleCustomerChange]);
    
    // Effect to ensure a user cannot be both the assignee and tagged in the same task.
    useEffect(() => {
        if (selectedAssigneeId && selectedTaggedUserIds.includes(selectedAssigneeId)) {
            // If the assignee is also in the tagged list, remove them from the tagged list.
            setSelectedTaggedUserIds(prevIds => prevIds.filter(id => id !== selectedAssigneeId));
        }
    }, [selectedAssigneeId, selectedTaggedUserIds]);
    
    // Effect to manage the available options in the date and time dropdowns.
    // It prevents users from selecting a date/time in the past.
    useEffect(() => {
        const now = new Date();
        // If the selected date is in the past, reset it to now.
        if (dueDate < now) { setDueDate(now); return; }

        const year = dueDate.getFullYear(); const month = dueDate.getMonth() + 1; const day = dueDate.getDate(); const hour = dueDate.getHours();
        
        // Filter months, days, hours, and minutes based on the current time.
        const isCurrentYear = year === now.getFullYear();
        const startMonth = isCurrentYear ? now.getMonth() + 1 : 1;
        setFilteredMonths(allMonths.filter(m => parseInt(m.value) >= startMonth));

        const isCurrentMonth = isCurrentYear && month === (now.getMonth() + 1);
        const startDay = isCurrentMonth ? now.getDate() : 1;
        const allDaysInMonth = Array.from({ length: getDaysInMonth(year, month) }, (_, i) => i + 1);
        setFilteredDays(allDaysInMonth.filter(d => d >= startDay).map(String));

        const isCurrentDay = isCurrentMonth && day === now.getDate();
        const startHour = isCurrentDay ? now.getHours() : 0;
        setFilteredHours(allHours.filter(h => parseInt(h) >= startHour));
        
        const isCurrentHour = isCurrentDay && hour === now.getHours();
        const startMinute = isCurrentHour ? now.getMinutes() : 0;
        setFilteredMinutes(allMinutes.filter(m => parseInt(m) >= startMinute));
        
        // Calculate the maximum number of days for the reminder input based on the due date.
        const today = new Date(); today.setHours(0, 0, 0, 0); const selectedDateOnly = new Date(year, month - 1, day);
        if (selectedDateOnly < today) { setMaxReminderDays(0); if (remindMeDays !== 1) setRemindMeDays(1); }
        else { const diffTime = selectedDateOnly.getTime() - today.getTime(); const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); setMaxReminderDays(diffDays); if (remindMeDays > diffDays) { setRemindMeDays(diffDays > 0 ? diffDays : 1); } }
    }, [dueDate, remindMeDays]); // Re-runs whenever the due date changes.
    
    /**
     * Handles changes to any part of the date/time selectors.
     * @param {'year'|'month'|'day'|'hour'|'minute'} part - The part of the date to change.
     * @param {string} value - The new value.
     */
    const handleDateTimeChange = (part, value) => {
        const newDate = new Date(dueDate);
        switch (part) {
            case 'year': newDate.setFullYear(parseInt(value, 10)); break;
            case 'month': newDate.setMonth(parseInt(value, 10) - 1); break;
            case 'day': newDate.setDate(parseInt(value, 10)); break;
            case 'hour': newDate.setHours(parseInt(value, 10)); break;
            case 'minute': newDate.setMinutes(parseInt(value, 10)); break;
            default: break;
        }
        setDueDate(newDate);
    };

    // --- File Attachment Handlers ---

    /**
     * Adds new files to the state, ensuring no duplicates are added.
     * Wrapped in `useCallback` for optimization as it's used in drop handlers.
     */
    const addFilesToState = useCallback((newFiles) => {
        setFiles(prevFiles => {
            const existingFileNames = new Set(prevFiles.map(f => f.name));
            const uniqueNewFiles = newFiles.filter(nf => !existingFileNames.has(nf.name));
            // Add a corresponding empty description for each new file.
            setFileDescriptions(prevDesc => [...prevDesc, ...Array(uniqueNewFiles.length).fill('')]);
            return [...prevFiles, ...uniqueNewFiles];
        });
    }, []);

    const handleFileChange = (e) => { if (e.target.files) { addFilesToState(Array.from(e.target.files)); } e.target.value = null; }; // Reset input value to allow re-selecting the same file.
    const handleRemoveFile = (indexToRemove) => { setFiles(prev => prev.filter((_, i) => i !== indexToRemove)); setFileDescriptions(prev => prev.filter((_, i) => i !== indexToRemove)); };
    const handleDescriptionChange = (index, value) => { setFileDescriptions(prev => prev.map((desc, i) => (i === index ? value : desc))); };
    
    // Drag and drop event handlers for the file upload zone.
    const handleFileDrop = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); if (e.dataTransfer.files && e.dataTransfer.files.length > 0) { addFilesToState(Array.from(e.dataTransfer.files)); e.dataTransfer.clearData(); } }, [addFilesToState]);
    const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); }, []);
    const handleDragEnter = useCallback((e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer.items && e.dataTransfer.items.length > 0) { setIsDragging(true); } }, []);
    const handleDragLeave = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }, []);

    
    // --- Subtask Handlers ---
    // These functions manage the state of the subtasks array.
    
    /**
     * Fetches reporting managers for a specific subtask based on its assignee.
     * Similar to the parent handler but updates state within the `subtasks` array.
     */
    const fetchManagersForSubtask = async (subtaskId, assigneeId) => {
        if (!assigneeId) return;
        setSubtasks(prev => prev.map(st => st.id === subtaskId ? { ...st, isFetchingManagers: true } : st));
        try {
            const managersData = await getReportingManagers(assigneeId);
            const self = allUsers.find(a => String(a.id) === String(assigneeId));
            let formattedManagers = (managersData && managersData.length > 0)
                ? managersData.map(manager => allUsers.find(a => a.email === manager.managerEmail)).filter(Boolean).map(u => ({ id: String(u.id), name: u.name }))
                : (self ? [{ id: String(self.id), name: self.name }] : []);

            const sortedManagers = sortAlphabetically(formattedManagers, 'name');
            const loggedInUserId = String(user.id);
            const isCreatorInManagerList = sortedManagers.some(m => String(m.id) === loggedInUserId);
            let finalManagerId = isCreatorInManagerList ? loggedInUserId : (sortedManagers.length === 1 ? sortedManagers[0].id : '');
            
            setSubtasks(prev => prev.map(st => (st.id === subtaskId) ? { ...st, reportingManagers: sortedManagers, reportingManagerId: finalManagerId, isFetchingManagers: false } : st));
        } catch (error) {
            console.error(`Failed to fetch managers for subtask ${subtaskId}:`, error);
            setSubtasks(prev => prev.map(st => st.id === subtaskId ? { ...st, isFetchingManagers: false } : st));
        }
    };

    /**
     * Adds a new, empty subtask object to the `subtasks` array.
     */
    const handleAddSubtask = () => {
        const currentUserRole = user?.role?.name.toUpperCase();
        const newSubtaskId = Date.now(); // Use a temporary unique ID for React's key prop.
        const defaultAssigneeId = currentUserRole === 'USER' ? String(user.id) : ''; // Auto-assign if role is USER.

        setSubtasks(prev => [...prev, {
            id: newSubtaskId,
            title: '',
            description: '',
            assigneeId: defaultAssigneeId,
            reportingManagerId: '',
            taggedUserIds: [],
            dueDate: new Date(dueDate), // Inherit due date from parent initially.
            status: selectedStatus, // Inherit status.
            priority: selectedPriority, // Inherit priority.
            reportingManagers: [],
            isFetchingManagers: false,
            invalidFields: [],
            files: [],
            fileDescriptions: [],
            isDragging: false,
        }]);
        
        if (defaultAssigneeId) {
            fetchManagersForSubtask(newSubtaskId, defaultAssigneeId);
        }
    };
    
    const handleRemoveSubtask = (id) => { setSubtasks(prev => prev.filter(st => st.id !== id)); };
    
    /**
     * A generic handler to update a field for a specific subtask.
     */
    const handleSubtaskChange = (id, field, value) => {
        setSubtasks(prev => prev.map(st => {
            if (st.id === id) {
                const updatedSubtask = { ...st, [field]: value };
                if (field === 'assigneeId') {
                    // When subtask assignee changes, clear the manager and fetch new ones.
                    updatedSubtask.reportingManagerId = '';
                    updatedSubtask.reportingManagers = [];
                    if (updatedSubtask.taggedUserIds.includes(value)) {
                        updatedSubtask.taggedUserIds = updatedSubtask.taggedUserIds.filter(userId => userId !== value);
                    }
                    fetchManagersForSubtask(id, value);
                }
                // Clear validation error for the field being changed.
                if (st.invalidFields.includes(field)) {
                    updatedSubtask.invalidFields = st.invalidFields.filter(f => f !== field);
                }
                return updatedSubtask;
            }
            return st;
        }));
    };

    /**
     * Handles changes to a subtask's date/time, ensuring it doesn't exceed the parent's due date.
     */
    const handleSubtaskDateTimeChange = (subtaskId, part, value) => {
        setSubtasks(prevSubtasks => prevSubtasks.map(st => {
            if (st.id === subtaskId) {
                const newDate = new Date(st.dueDate);
                // Update the correct part of the date.
                switch (part) { case 'year': newDate.setFullYear(parseInt(value, 10)); break; case 'month': newDate.setMonth(parseInt(value, 10) - 1, 1); break; case 'day': newDate.setDate(parseInt(value, 10)); break; case 'hour': newDate.setHours(parseInt(value, 10)); break; case 'minute': newDate.setMinutes(parseInt(value, 10)); break; default: break; }
                const parentDueDate = new Date(dueDate);
                // Enforce constraints: subtask due date cannot be after parent's or in the past.
                if (newDate > parentDueDate) { return { ...st, dueDate: parentDueDate }; }
                if (newDate < new Date()) { return { ...st, dueDate: new Date() }; }

                return { ...st, dueDate: newDate, invalidFields: st.invalidFields.filter(f => f !== 'dueDate') };
            }
            return st;
        }));
    };

    // --- Subtask Attachment Handlers ---
    const addFilesToSubtaskState = useCallback((subtaskId, newFiles) => {
        setSubtasks(prevSubtasks => prevSubtasks.map(st => {
            if (st.id === subtaskId) {
                const existingFileNames = new Set(st.files.map(f => f.name));
                const uniqueNewFiles = newFiles.filter(nf => !existingFileNames.has(nf.name));
                return {
                    ...st,
                    files: [...st.files, ...uniqueNewFiles],
                    fileDescriptions: [...st.fileDescriptions, ...Array(uniqueNewFiles.length).fill('')]
                };
            }
            return st;
        }));
    }, []);

    const handleSubtaskFileChange = (subtaskId, e) => {
        if (e.target.files) {
            addFilesToSubtaskState(subtaskId, Array.from(e.target.files));
        }
        e.target.value = null; 
    };

    const handleRemoveSubtaskFile = (subtaskId, indexToRemove) => {
        setSubtasks(prevSubtasks => prevSubtasks.map(st => {
            if (st.id === subtaskId) {
                return {
                    ...st,
                    files: st.files.filter((_, i) => i !== indexToRemove),
                    fileDescriptions: st.fileDescriptions.filter((_, i) => i !== indexToRemove)
                };
            }
            return st;
        }));
    };

    const handleSubtaskFileDescriptionChange = (subtaskId, index, value) => {
        setSubtasks(prevSubtasks => prevSubtasks.map(st => {
            if (st.id === subtaskId) {
                return {
                    ...st,
                    fileDescriptions: st.fileDescriptions.map((desc, i) => (i === index ? value : desc))
                };
            }
            return st;
        }));
    };

    const handleSubtaskFileDrop = useCallback((subtaskId, e) => {
        e.preventDefault();
        e.stopPropagation();
        setSubtasks(prev => prev.map(st => st.id === subtaskId ? { ...st, isDragging: false } : st));
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            addFilesToSubtaskState(subtaskId, Array.from(e.dataTransfer.files));
            e.dataTransfer.clearData();
        }
    }, [addFilesToSubtaskState]);
    
    const handleSubtaskDragEnter = useCallback((subtaskId, e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setSubtasks(prev => prev.map(st => st.id === subtaskId ? { ...st, isDragging: true } : st));
        }
    }, []);

    const handleSubtaskDragLeave = useCallback((subtaskId, e) => {
        e.preventDefault();
        e.stopPropagation();
        setSubtasks(prev => prev.map(st => st.id === subtaskId ? { ...st, isDragging: false } : st));
    }, []);
    
    
    /**
     * Calculates the valid date/time options for a subtask's dropdowns.
     * The range is between "now" and the parent task's due date.
     */
    const getSubtaskDateTimeOptions = (currentSubtaskDate, parentDueDate) => {
        const now = new Date();
        const maxDate = new Date(parentDueDate);

        const year = currentSubtaskDate.getFullYear(); const month = currentSubtaskDate.getMonth() + 1; const day = currentSubtaskDate.getDate(); const hour = currentSubtaskDate.getHours();

        const availableYears = years.filter(y => y >= now.getFullYear() && y <= maxDate.getFullYear());
        const startMonth = year === now.getFullYear() ? now.getMonth() + 1 : 1; const endMonth = year === maxDate.getFullYear() ? maxDate.getMonth() + 1 : 12;
        const filteredMonths = allMonths.filter(m => parseInt(m.value) >= startMonth && parseInt(m.value) <= endMonth);

        const startDay = (year === now.getFullYear() && month === now.getMonth() + 1) ? now.getDate() : 1; const endDay = (year === maxDate.getFullYear() && month === maxDate.getMonth() + 1) ? maxDate.getDate() : getDaysInMonth(year, month);
        const filteredDays = Array.from({ length: getDaysInMonth(year, month) }, (_, i) => i + 1).filter(d => d >= startDay && d <= endDay).map(String);

        const startHour = (year === now.getFullYear() && month === now.getMonth() + 1 && day === now.getDate()) ? now.getHours() : 0; const endHour = (year === maxDate.getFullYear() && month === maxDate.getMonth() + 1 && day === maxDate.getDate()) ? maxDate.getHours() : 23;
        const filteredHours = allHours.filter(h => parseInt(h) >= startHour && parseInt(h) <= endHour);

        const startMinute = (year === now.getFullYear() && month === now.getMonth() + 1 && day === now.getDate() && hour === now.getHours()) ? now.getMinutes() : 0; const endMinute = (year === maxDate.getFullYear() && month === maxDate.getMonth() + 1 && day === maxDate.getDate() && hour === maxDate.getHours()) ? maxDate.getMinutes() : 59;
        const filteredMinutes = allMinutes.filter(m => parseInt(m) >= startMinute && parseInt(m) <= endMinute);
        
        return { availableYears, filteredMonths, filteredDays, filteredHours, filteredMinutes };
    };

    // --- Form Submission Handler ---

    /**
     * Handles the form submission for creating the task and/or subtasks.
     * Includes validation, payload creation, and API calls.
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!hasPermission(PERMISSIONS.CREATE_TASK)) { setFormValidationError("You do not have permission to perform this action."); return; }
        setFormValidationError(null); setInvalidFields([]); setSuccessMessage('');

        const createAndUpload = async (payload, attachments, descriptions) => {
            const response = await createTask(payload);
            const newTaskId = response?.newTask?.taskId;
            if (!newTaskId) throw new Error("Task created, but a task ID was not returned.");
            if (attachments && attachments.length > 0) {
                await uploadAttachments(newTaskId, 'attachment_master', attachments, descriptions);
            }
            return newTaskId;
        };

        // --- SUBTASK-ONLY MODE SUBMISSION LOGIC ---
        if (isSubtaskOnlyMode) {
            let allSubtasksValid = true;
            // Validate all subtasks.
            const validatedSubtasks = subtasks.map(st => {
                const subtaskInvalidFields = [];
                if (!st.title.trim()) subtaskInvalidFields.push('title');
                if (!st.assigneeId) subtaskInvalidFields.push('assignee');
                if (!st.reportingManagerId) subtaskInvalidFields.push('reportingManager');
                if (new Date(st.dueDate) > new Date(dueDate)) subtaskInvalidFields.push('dueDate');
                
                if (subtaskInvalidFields.length > 0) { allSubtasksValid = false; return { ...st, invalidFields: subtaskInvalidFields }; }
                return st;
            });

            if (!allSubtasksValid || subtasks.length === 0) {
                setSubtasks(validatedSubtasks);
                setFormValidationError('Please fill in all required fields (*) for all subtasks.');
                return;
            }
            
            setIsSubmitting(true);
            try {
                // Create an array of promises, one for each subtask creation and upload process.
                const subtaskCreationPromises = subtasks.map(st => {
                    const subtaskUser = allUsers.find(u => String(u.id) === String(st.assigneeId));
                    const subtaskManager = allUsers.find(u => String(u.id) === String(st.reportingManagerId));
                    const stDueDate = new Date(st.dueDate);
                    const subtaskPayload = {
                        parentTaskId: parentTask.taskId,
                        title: autoCorrectGrammar(st.title).trim(),
                        description: autoCorrectGrammar(st.description).trim(),
                        status: st.status.toLowerCase(),
                        priority: st.priority,
                        assigneeName: subtaskUser.name,
                        reportingManagerId: subtaskManager ? parseInt(subtaskManager.id, 10) : 0,
                        dueDate: `${stDueDate.getFullYear()}-${pad(stDueDate.getMonth() + 1)}-${pad(stDueDate.getDate())}T${pad(stDueDate.getHours())}:${pad(stDueDate.getMinutes())}:00.000Z`,
                        category: selectedCategory,
                        projectName: allProjects.find(p => String(p.id) === String(selectedProject))?.name || "",
                        assetName: [], 
                        taggedUsers: st.taggedUserIds.map(id => parseInt(id, 10)),
                        reminderDays: 1, 
                        customerId: selectedCustomerId ? parseInt(selectedCustomerId, 10) : 0,
                    };
                    return createAndUpload(subtaskPayload, st.files, st.fileDescriptions);
                });
                
                const results = await Promise.allSettled(subtaskCreationPromises);
                const failedSubtasks = results.filter(res => res.status === 'rejected');
                if (failedSubtasks.length > 0) {
                     setFormValidationError(`${results.length - failedSubtasks.length} subtask(s) created, but ${failedSubtasks.length} failed.`);
                    setIsSubmitting(false);
                } else {
                    setSuccessMessage(`${subtasks.length} subtask(s) created successfully! Redirecting...`);
                    setTimeout(() => { navigate(`/tasks/${parentTask.taskId}`); }, 2000);
                }
            } catch (err) {
                 setFormValidationError(err.message || 'An unknown API error occurred while creating subtasks.');
                setIsSubmitting(false);
            }
            return; // End subtask-only submission.
        }

        // --- REGULAR TASK & SUBTASK CREATION LOGIC ---
        // 1. Validate the parent task fields.
        const parentInvalidFields = [];
        if (!title.trim()) parentInvalidFields.push('title');
        if (!selectedAssigneeId) parentInvalidFields.push('assignee');
        if (!selectedReportingManagerId) parentInvalidFields.push('reportingManager');
        if (!selectedStatus) parentInvalidFields.push('status');
        if (!selectedPriority) parentInvalidFields.push('priority');
        if (!selectedCategory) parentInvalidFields.push('category');
        if (!dueDate) parentInvalidFields.push('dueDate');
        
        // 2. Validate all subtasks.
        let allSubtasksValid = true;
        const validatedSubtasks = subtasks.map(st => {
            const subtaskInvalidFields = [];
            if (!st.title.trim()) subtaskInvalidFields.push('title');
            if (!st.assigneeId) subtaskInvalidFields.push('assignee');
            if (!st.reportingManagerId) subtaskInvalidFields.push('reportingManager');
            if (new Date(st.dueDate) > new Date(dueDate)) subtaskInvalidFields.push('dueDate');
            
            if (subtaskInvalidFields.length > 0) { allSubtasksValid = false; return { ...st, invalidFields: subtaskInvalidFields }; }
            return st;
        });

        if (parentInvalidFields.length > 0 || !allSubtasksValid) {
            setInvalidFields(parentInvalidFields);
            setSubtasks(validatedSubtasks);
            setFormValidationError('Please fill in all required fields (*) for the main task and all subtasks.');
            return;
        }
        
        // 3. Additional business logic validation (e.g., due date vs. project end date).
        const parentTaskDueDate = new Date(dueDate);
        if (companyKeyword !== 'PL' && selectedProject) {
            const project = allProjects.find(p => String(p.id) === String(selectedProject));
            if (project && project.endDate && parentTaskDueDate > new Date(project.endDate)) {
                const formattedEndDate = `${pad(new Date(project.endDate).getDate())}-${pad(new Date(project.endDate).getMonth() + 1)}-${new Date(project.endDate).getFullYear()}`;
                setFormValidationError(`Due date cannot be after the project's end date of ${formattedEndDate}.`);
                setInvalidFields(['dueDate', 'project']);
                return;
            }
        }

        setIsSubmitting(true);

        // 4. Construct the parent task payload.
        const parentUser = allUsers.find(u => String(u.id) === String(selectedAssigneeId));
        const parentManager = allUsers.find(u => String(u.id) === String(selectedReportingManagerId));
        const parentPayload = {
            title: autoCorrectGrammar(title).trim(),
            description: autoCorrectGrammar(description).trim(),
            status: selectedStatus.toLowerCase(),
            priority: selectedPriority,
            assigneeName: parentUser.name,
            dueDate: `${parentTaskDueDate.getFullYear()}-${pad(parentTaskDueDate.getMonth() + 1)}-${pad(parentTaskDueDate.getDate())}T${pad(parentTaskDueDate.getHours())}:${pad(parentTaskDueDate.getMinutes())}:00.000Z`,
            category: selectedCategory,
            projectName: allProjects.find(p => String(p.id) === String(selectedProject))?.name || "",
            assetName: selectedAssetIds.map(id => assets.find(a => a.id === id)?.asset_code).filter(Boolean),
            taggedUsers: selectedTaggedUserIds.map(id => parseInt(id, 10)),
            reminderDays: remindMeDays,
            customerId: selectedCustomerId ? parseInt(selectedCustomerId, 10) : 0,
            reportingManagerId: parentManager ? parseInt(parentManager.id, 10) : 0,
            comments: "", remarks: ""
        };
        
        try {
            // 5. Create the parent task and upload its files.
            const parentTaskId = await createAndUpload(parentPayload, files, fileDescriptions);

            // 6. If there are subtasks, create them and upload their files.
            if (subtasks.length > 0) {
                const subtaskCreationPromises = subtasks.map(st => {
                    const subtaskUser = allUsers.find(u => String(u.id) === String(st.assigneeId));
                    const subtaskManager = allUsers.find(u => String(u.id) === String(st.reportingManagerId));
                    const stDueDate = new Date(st.dueDate);
                    const subtaskPayload = {
                        ...parentPayload, // Inherit fields like category, project, etc.
                        parentTaskId: parentTaskId,
                        title: autoCorrectGrammar(st.title).trim(),
                        description: autoCorrectGrammar(st.description).trim(),
                        status: st.status.toLowerCase(),
                        priority: st.priority,
                        assigneeName: subtaskUser.name,
                        reportingManagerId: subtaskManager ? parseInt(subtaskManager.id, 10) : 0,
                        dueDate: `${stDueDate.getFullYear()}-${pad(stDueDate.getMonth() + 1)}-${pad(stDueDate.getDate())}T${pad(stDueDate.getHours())}:${pad(stDueDate.getMinutes())}:00.000Z`,
                        assetName: [], 
                        taggedUsers: st.taggedUserIds.map(id => parseInt(id, 10)),
                    };
                    return createAndUpload(subtaskPayload, st.files, st.fileDescriptions);
                });
                
                const results = await Promise.allSettled(subtaskCreationPromises);
                const failedSubtasks = results.filter(res => res.status === 'rejected');
                if (failedSubtasks.length > 0) {
                    setFormValidationError(`Parent task created successfully, but ${failedSubtasks.length} subtask(s) failed to create.`);
                    setIsSubmitting(false);
                } else {
                    setSuccessMessage('Task and all subtasks created successfully! Redirecting...');
                    setTimeout(() => { navigate('/tasks'); }, 2000);
                }
            } else {
                setSuccessMessage('Task created successfully! Redirecting...');
                setTimeout(() => { navigate('/tasks'); }, 2000);
            }
        } catch (err) {
            setFormValidationError(err.response?.data?.message || err.message || 'An unknown API error occurred.');
            setIsSubmitting(false);
        }
    };
    

    // --- Conditional Rendering ---
    
    // Show a skeleton loader while permissions or initial data are loading.
    if (isPermissionsLoading || isFetchingInitialData) {
        return <CreateTaskPageSkeleton />;
    }
    // If the user does not have permission, show the access denied page.
    if (!hasPermission(PERMISSIONS.CREATE_TASK)) {
        return <AccessDenied />;
    }

    // --- Render-Time Variable Calculations ---
    const userRole = user?.role?.name.toUpperCase();
    
    // Determine if various form fields should be disabled based on state.
    const isAssigneeDropdownDisabled = isSubmitting || isSubtaskOnlyMode || (userRole !== 'SUPER_ADMIN' && assigneeOptions.length <= 1);
    const isCreatorAnAutoSelectedManager = reportingManagers.some(m => String(m.id) === String(user.id)) && selectedReportingManagerId === String(user.id);
    const isReportingManagerDropdownDisabled = isSubmitting || isFetchingManagers || !selectedAssigneeId || reportingManagers.length <= 1 || isSubtaskOnlyMode || isCreatorAnAutoSelectedManager;
    const isParentFieldDisabled = isSubmitting || isSubtaskOnlyMode;
    
    // --- JSX Rendering ---
    return (
        <div className="create-task-page">
            <div className="page-header">
                {/* Back link destination depends on whether we are in subtask-only mode */}
                <Link to={isSubtaskOnlyMode && parentTask ? `/tasks/${parentTask.taskId}` : "/tasks"} className="back-link"><IoArrowBack /> Back to {isSubtaskOnlyMode ? 'Task Details' : 'Tasks'}</Link>
                <h1>{isSubtaskOnlyMode ? `Add Subtask to: ${parentTask?.title || ''}` : 'Create New Task'}</h1>
                <p>{isSubtaskOnlyMode ? 'Fill details for the new subtask(s).' : 'Fill details to create a new task and optionally add subtasks.'}</p>
            </div>

            {/* Display success or error messages at the top of the form */}
            {successMessage && <div className="success-message" role="alert">{successMessage}</div>}
            {formValidationError && <div className="error-message icon-flex" role="alert" style={{ marginBottom: '15px' }}><IoAlertCircleOutline size={20} />{formValidationError}</div>}
            
            <form onSubmit={handleSubmit} className="create-task-form" noValidate>
                {/* Main panel for task details and subtasks */}
                <div className="form-main-panel">
                    <h2>Task Information</h2>
                    {/* Each form group contains a label and a form control (input, dropdown, etc.) */}
                    <div className="form-group"><label htmlFor="category">Category <span className="required-asterisk">*</span></label><SearchableDropdown options={categories} selectedValues={selectedCategory} onChange={(value) => handleFieldChange('category', value, setSelectedCategory)} placeholder="Select a Category" labelField="value" valueField="value" disabled={isParentFieldDisabled || !hasPermission(PERMISSIONS.VIEW_CATEGORY)} error={invalidFields.includes('category')} dataLoadError={dataLoadErrors.categories} />{invalidFields.includes('category') && <small className="field-error-message">Category is required.</small>}</div>
                    <div className="form-group"><label htmlFor="title">Title <span className="required-asterisk">*</span></label><input type="text" id="title" value={title} onChange={(e) => handleFieldChange('title', autoCorrectGrammar(e.target.value), setTitle)} placeholder="Enter task title" required disabled={isParentFieldDisabled} className={invalidFields.includes('title') ? 'invalid-field' : ''} />{invalidFields.includes('title') && <small className="field-error-message">Title is required.</small>}</div>
                    <div className="form-group"><label htmlFor="description">Description</label><textarea id="description" value={description} onChange={(e) => setDescription(autoCorrectGrammar(e.target.value))} placeholder="Provide a detailed description" rows="4" disabled={isParentFieldDisabled} maxLength={DESCRIPTION_MAX_LENGTH} /><small className="char-counter">{DESCRIPTION_MAX_LENGTH - description.length} characters remaining</small></div>
                    <div className="form-group"><label>Customer (Optional)</label><SearchableDropdown options={customers} selectedValues={selectedCustomerId} onChange={handleCustomerChange} placeholder="Select a Customer" labelField="name" valueField="id" disabled={isParentFieldDisabled || !hasPermission(PERMISSIONS.VIEW_PROJECT) || isFetchingProjectForAsset} dataLoadError={dataLoadErrors.customers} allowClear={true}/>{dataLoadErrors.customers && <small className="field-error-message">{dataLoadErrors.customers}</small>}{!isSubtaskOnlyMode && hasPermission(PERMISSIONS.VIEW_PROJECT) && ( <div className="field-action-container"><Link to="/customers/new" className="field-action-button">+ Create New Customer</Link></div>)}</div>
                    <div className="form-group"><label>Project (Optional)</label><SearchableDropdown options={filteredProjects} selectedValues={selectedProject} onChange={handleProjectChange} placeholder={isFetchingProjects ? "Loading projects..." : "Select a Project"} labelField="name" valueField="id" disabled={isParentFieldDisabled || isFetchingProjects || !hasPermission(PERMISSIONS.VIEW_PROJECT) || isFetchingProjectForAsset} dataLoadError={dataLoadErrors.projects} allowClear={true} error={invalidFields.includes('project')}/>{dataLoadErrors.projects && <small className="field-error-message">{dataLoadErrors.projects}</small>}{!isSubtaskOnlyMode && hasPermission(PERMISSIONS.VIEW_PROJECT) && ( <div className="field-action-container"><Link to="/project" className="field-action-button">+ Create New Project</Link></div>)}</div>
                    <div className="form-group"><label>Assets (Optional)</label><SearchableDropdown options={assets} selectedValues={selectedAssetIds} onChange={(values) => handleFieldChange('assets', values, setSelectedAssetIds)} placeholder="Select Assets" isMultiSelect={true} labelField="value" valueField="id" disabled={isParentFieldDisabled} dataLoadError={dataLoadErrors.assets}/>{dataLoadErrors.assets && <small className="field-error-message">{dataLoadErrors.assets}</small>}</div>
                    <div className="form-row"><div className="form-group"><label htmlFor="status">Status <span className="required-asterisk">*</span></label><SearchableDropdown options={statuses} selectedValues={selectedStatus} onChange={(value) => handleFieldChange('status', value, setSelectedStatus)} placeholder="Select a Status" labelField="displayValue" valueField="value" disabled={isParentFieldDisabled || !hasPermission(PERMISSIONS.VIEW_STATUS)} error={invalidFields.includes('status')} dataLoadError={dataLoadErrors.statuses}/>{invalidFields.includes('status') && <small className="field-error-message">Status required.</small>}</div><div className="form-group"><label htmlFor="priority">Priority <span className="required-asterisk">*</span></label><SearchableDropdown options={priorities} selectedValues={selectedPriority} onChange={(value) => handleFieldChange('priority', value, setSelectedPriority)} placeholder="Select a Priority" labelField="value" valueField="value" disabled={isParentFieldDisabled || !hasPermission(PERMISSIONS.VIEW_PRIORITY)} error={invalidFields.includes('priority')} dataLoadError={dataLoadErrors.priorities}/>{invalidFields.includes('priority') && <small className="field-error-message">Priority required.</small>}</div></div>
                    
                    {/* Attachment section is hidden in subtask-only mode */}
                    {!isSubtaskOnlyMode && (
                        <div className="attachments-section-form">
                            <h2>Attachments (Optional)</h2>
                            <label htmlFor="file-upload" className={`drop-zone ${isDragging ? 'drag-over' : ''}`} onDrop={handleFileDrop} onDragOver={handleDragOver} onDragEnter={handleDragEnter} onDragLeave={handleDragLeave}>
                                <input id="file-upload" type="file" multiple onChange={handleFileChange} className="file-input-hidden" disabled={isSubmitting}/>
                                <div className="drop-zone-content"><IoCloudUploadOutline /><span>{isDragging ? 'Drop files here' : 'Drag & Drop files or Click to Upload'}</span><small>You can add multiple files</small></div>
                            </label>
                            {files.length > 0 && (
                                <div className="file-list">
                                    {files.map((file, index) => (
                                        <div key={`${file.name}-${index}`} className="file-list-item">
                                            <div className="file-info"><span className="file-name" title={file.name}>{file.name}</span><input type="text" className="file-description-input" placeholder="Add a description (optional)..." value={fileDescriptions[index]} onChange={(e) => handleDescriptionChange(index, e.target.value)} disabled={isSubmitting}/></div>
                                            <button type="button" className="remove-file-btn" onClick={() => handleRemoveFile(index)} title="Remove file" disabled={isSubmitting}><IoTrashOutline /></button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Subtasks section is shown for all companies except 'PL', or always in subtask-only mode */}
                    {(isSubtaskOnlyMode || companyKeyword !== 'PL') && (
                        <div className="subtasks-section">
                            <div className="subtasks-header"><h2>{isSubtaskOnlyMode ? 'New Subtasks' : 'Subtasks (Optional)'}</h2><button type="button" onClick={handleAddSubtask} className="add-subtask-btn" disabled={isSubmitting}><IoAddCircleOutline /> Add Subtask</button></div>
                            <div className="subtasks-container">
                                {subtasks.map((st, index) => {
                                    const subtaskDate = new Date(st.dueDate);
                                    const { availableYears, filteredMonths, filteredDays, filteredHours, filteredMinutes } = getSubtaskDateTimeOptions(subtaskDate, dueDate);
                                    const isCreatorSubtaskManager = st.reportingManagers.some(m => String(m.id) === String(user.id)) && st.reportingManagerId === String(user.id);
                                    const isSubtaskManagerDropdownDisabled = isSubmitting || st.isFetchingManagers || !st.assigneeId || st.reportingManagers.length <= 1 || isCreatorSubtaskManager;
                                    const subtaskTaggableUsers = allUsers.filter(u => String(u.id) !== String(st.assigneeId));

                                    return (
                                    <div key={st.id} className="subtask-card">
                                        <div className="subtask-card-header"><h4>Subtask #{index + 1}</h4><button type="button" className="remove-file-btn" onClick={() => handleRemoveSubtask(st.id)} disabled={isSubmitting}><IoTrashOutline /></button></div>
                                        <div className="subtask-card-body">
                                            <div className="form-group"><label>Title <span className="required-asterisk">*</span></label><input type="text" value={st.title} onChange={(e) => handleSubtaskChange(st.id, 'title', autoCorrectGrammar(e.target.value))} placeholder="Enter subtask title" disabled={isSubmitting} className={st.invalidFields.includes('title') ? 'invalid-field' : ''} /></div>
                                            <div className="form-group"><label>Description</label><textarea value={st.description} onChange={(e) => handleSubtaskChange(st.id, 'description', autoCorrectGrammar(e.target.value))} rows="2" placeholder="Subtask description" disabled={isSubmitting} /></div>
                                            <div className="form-row">
                                                <div className="form-group"><label>Assignee <span className="required-asterisk">*</span></label><SearchableDropdown options={assigneeOptions} selectedValues={st.assigneeId} onChange={(val) => handleSubtaskChange(st.id, 'assigneeId', val)} placeholder="Select Assignee" disabled={isSubmitting || (userRole !== 'SUPER_ADMIN' && assigneeOptions.length <= 1)} error={st.invalidFields.includes('assignee')} /></div>
                                                <div className="form-group"><label>Reporting Manager <span className="required-asterisk">*</span></label><SearchableDropdown options={st.reportingManagers} selectedValues={st.reportingManagerId} onChange={(val) => handleSubtaskChange(st.id, 'reportingManagerId', val)} placeholder={st.isFetchingManagers ? "Loading..." : "Select Manager"} disabled={isSubtaskManagerDropdownDisabled} error={st.invalidFields.includes('reportingManager')} /></div>
                                            </div>
                                            {/* Tagging users is a 'ZO' specific feature */}
                                            {companyKeyword === 'ZO' && (<div className="form-group"><label>Tag Users (Optional)</label><SearchableDropdown options={subtaskTaggableUsers} selectedValues={st.taggedUserIds} onChange={(val) => handleSubtaskChange(st.id, 'taggedUserIds', val)} placeholder="Select users to tag" labelField="name" valueField="id" isMultiSelect={true} disabled={isSubmitting} dataLoadError={dataLoadErrors.users} /></div>)}
                                            <div className="form-row">
                                                <div className="form-group"><label>Status <span className="required-asterisk">*</span></label><SearchableDropdown options={statuses} selectedValues={st.status} onChange={(val) => handleSubtaskChange(st.id, 'status', val)} placeholder="Select Status" labelField="displayValue" valueField="value" disabled={isSubmitting} error={st.invalidFields.includes('status')} /></div>
                                                <div className="form-group"><label>Priority <span className="required-asterisk">*</span></label><SearchableDropdown options={priorities} selectedValues={st.priority} onChange={(val) => handleSubtaskChange(st.id, 'priority', val)} placeholder="Select Priority" labelField="value" valueField="value" disabled={isSubmitting} error={st.invalidFields.includes('priority')} /></div>
                                            </div>
                                            <div className="form-group">
                                                <label>Due Date & Time <span className="required-asterisk">*</span></label>
                                                <div className="custom-duedate-inputs">
                                                    <div className={`date-picker-dmy ${st.invalidFields.includes('dueDate') ? 'invalid-field' : ''}`}>
                                                        <select value={subtaskDate.getDate().toString()} onChange={(e) => handleSubtaskDateTimeChange(st.id, 'day', e.target.value)} disabled={isSubmitting}>{filteredDays.map(day => <option key={day} value={day}>{day}</option>)}</select>
                                                        <select value={(subtaskDate.getMonth() + 1).toString()} onChange={(e) => handleSubtaskDateTimeChange(st.id, 'month', e.target.value)} disabled={isSubmitting}>{filteredMonths.map(month => <option key={month.value} value={month.value}>{month.name}</option>)}</select>
                                                        <select value={subtaskDate.getFullYear().toString()} onChange={(e) => handleSubtaskDateTimeChange(st.id, 'year', e.target.value)} disabled={isSubmitting}>{availableYears.map(year => <option key={year} value={year}>{year}</option>)}</select>
                                                    </div>
                                                    <div className={`time-picker-24h ${st.invalidFields.includes('dueDate') ? 'invalid-field' : ''}`}>
                                                        <select value={pad(subtaskDate.getHours())} onChange={(e) => handleSubtaskDateTimeChange(st.id, 'hour', e.target.value)} disabled={isSubmitting}>{filteredHours.map(hour => <option key={hour} value={hour}>{hour}</option>)}</select>
                                                        <span>:</span>
                                                        <select value={pad(subtaskDate.getMinutes())} onChange={(e) => handleSubtaskDateTimeChange(st.id, 'minute', e.target.value)} disabled={isSubmitting}>{filteredMinutes.map(minute => <option key={minute} value={minute}>{minute}</option>)}</select>
                                                    </div>
                                                </div>
                                                {st.invalidFields.includes('dueDate') && <small className="field-error-message">Due date must be before the parent task.</small>}
                                            </div>
                                            {/* Subtask Attachment Section */}
                                            <div className="form-group">
                                                <label>Attachments (Optional)</label>
                                                 <label htmlFor={`file-upload-${st.id}`} className={`drop-zone ${st.isDragging ? 'drag-over' : ''}`} onDrop={(e) => handleSubtaskFileDrop(st.id, e)} onDragOver={handleDragOver} onDragEnter={(e) => handleSubtaskDragEnter(st.id, e)} onDragLeave={(e) => handleSubtaskDragLeave(st.id, e)}>
                                                    <input id={`file-upload-${st.id}`} type="file" multiple onChange={(e) => handleSubtaskFileChange(st.id, e)} className="file-input-hidden" disabled={isSubmitting}/>
                                                    <div className="drop-zone-content">
                                                        <IoCloudUploadOutline />
                                                        <span>{st.isDragging ? 'Drop files here' : 'Drag & Drop files or Click to Upload'}</span>
                                                        <small>You can add multiple files</small>
                                                    </div>
                                                </label>
                                                {st.files.length > 0 && (
                                                    <div className="file-list">
                                                        {st.files.map((file, fileIndex) => (
                                                            <div key={`${file.name}-${fileIndex}`} className="file-list-item">
                                                                <div className="file-info"><span className="file-name" title={file.name}>{file.name}</span><input type="text" className="file-description-input" placeholder="Add a description..." value={st.fileDescriptions[fileIndex]} onChange={(e) => handleSubtaskFileDescriptionChange(st.id, fileIndex, e.target.value)} disabled={isSubmitting}/></div>
                                                                <button type="button" className="remove-file-btn" onClick={() => handleRemoveSubtaskFile(st.id, fileIndex)} title="Remove file" disabled={isSubmitting}><IoTrashOutline /></button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )})}
                            </div>
                        </div>
                    )}
                </div>

                {/* Side panel for assignment and due date */}
                <div className="form-side-panel">
                    <div className="assignment-section">
                        <h2>Assignment</h2>
                        <div className="form-group"><label htmlFor="assignee">Assignee <span className="required-asterisk">*</span></label><SearchableDropdown options={assigneeOptions} selectedValues={selectedAssigneeId} onChange={handleAssigneeChange} placeholder="Select an Assignee" labelField="name" valueField="id" disabled={isAssigneeDropdownDisabled} error={invalidFields.includes('assignee')} dataLoadError={dataLoadErrors.users}/>{invalidFields.includes('assignee') && <small className="field-error-message">Assignee is required.</small>}</div>
                        <div className="form-group"><label>Reporting Manager <span className="required-asterisk">*</span></label><SearchableDropdown options={reportingManagers} selectedValues={selectedReportingManagerId} onChange={(value) => handleFieldChange('reportingManager', value, setSelectedReportingManagerId)} placeholder={isFetchingManagers ? "Loading..." : "Select a Manager"} labelField="name" valueField="id" disabled={isReportingManagerDropdownDisabled} dataLoadError={dataLoadErrors.managers} error={invalidFields.includes('reportingManager')}/>{!selectedAssigneeId && <small>Please select an assignee first.</small>}{invalidFields.includes('reportingManager') && <small className="field-error-message">Reporting Manager is required.</small>}</div>
                        {companyKeyword === 'ZO' && (<div className="form-group"><label>Tag Users (Optional)</label><SearchableDropdown options={taggableUsers} selectedValues={selectedTaggedUserIds} onChange={setSelectedTaggedUserIds} placeholder="Select users to tag" labelField="name" valueField="id" isMultiSelect={true} disabled={isParentFieldDisabled} dataLoadError={dataLoadErrors.users} /></div>)}
                        <div className="form-group"><label>Department</label><input type="text" value={selectedDepartment} readOnly placeholder="Assignee's department" disabled /><small>Automatically filled based on assignee.</small></div>
                    </div>
                    <div className="duedate-section">
                        <h2>Due Date</h2>
                        <div className="form-group"><label>Due Date & Time <span className="required-asterisk">*</span></label><div className="custom-duedate-inputs"><div className={`date-picker-dmy ${invalidFields.includes('dueDate') ? 'invalid-field' : ''}`}><select value={dueDate.getDate().toString()} onChange={(e) => handleDateTimeChange('day', e.target.value)} disabled={isParentFieldDisabled} aria-label="Due Day">{filteredDays.map(day => <option key={day} value={day}>{day}</option>)}</select><select value={(dueDate.getMonth() + 1).toString()} onChange={(e) => handleDateTimeChange('month', e.target.value)} disabled={isParentFieldDisabled} aria-label="Due Month">{filteredMonths.map(month => <option key={month.value} value={month.value}>{month.name}</option>)}</select><select value={dueDate.getFullYear().toString()} onChange={(e) => handleDateTimeChange('year', e.target.value)} disabled={isParentFieldDisabled} aria-label="Due Year">{availableYears.map(year => <option key={year} value={year}>{year}</option>)}</select></div><div className={`time-picker-24h ${invalidFields.includes('dueDate') ? 'invalid-field' : ''}`}><select value={pad(dueDate.getHours())} onChange={(e) => handleDateTimeChange('hour', e.target.value)} disabled={isParentFieldDisabled} aria-label="Due Hour">{filteredHours.map(hour => <option key={hour} value={hour}>{hour}</option>)}</select><span>:</span><select value={pad(dueDate.getMinutes())} onChange={(e) => handleDateTimeChange('minute', e.target.value)} disabled={isParentFieldDisabled} aria-label="Due Minute">{filteredMinutes.map(minute => <option key={minute} value={minute}>{minute}</option>)}</select></div></div>{(invalidFields.includes('dueDate') || invalidFields.includes('dueTime')) && <small className="field-error-message">A valid due date and time are required.</small>}</div>
                    </div>
                    {!isSubtaskOnlyMode && (<div className="reminder-section"><h2>Reminder</h2><div className="form-group"><label htmlFor="remindMeDays">Remind Me Before Due Date In Days</label><input type="number" id="remindMeDays" value={remindMeDays} onChange={(e) => setRemindMeDays(Number(e.target.value))} min="1" max={maxReminderDays > 0 ? maxReminderDays : 1} disabled={isSubmitting || maxReminderDays <= 0} className="form-input"/><small>{maxReminderDays <= 0 ? "Reminder cannot be set for a past date." : "In days. Max: " + maxReminderDays}</small></div></div>)}
                </div>

                {/* Main submit button at the bottom */}
                <div className="form-actions"><button type="submit" className="create-task-btn" disabled={isSubmitting || isFetchingInitialData}>{isSubmitting ? 'Creating...' : isSubtaskOnlyMode ? 'Create Subtask(s)' : 'Create Task'}</button></div>
            </form>
        </div>
    );
};

export default CreateTaskPage;